<template>
  <div class="max-w-7xl mx-auto pb-10 relative">

    <div
      class="space-y-6 transition-all duration-300 animate-fade-in"
      :class="{ 'blur-sm pointer-events-none opacity-50': showModal }"
    >
      <!-- Encabezado -->
      <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 class="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">
            Tarifario y Tipos de Servicio (LLEVO)
          </h1>
          <p class="text-slate-500 dark:text-slate-400 text-sm">
            Configura los precios por kilómetro, banderazo base y costos por paradas adicionales.
          </p>
        </div>
        <button
          @click="openCreate"
          class="flex items-center gap-2 px-5 py-2.5 bg-[#F36421] hover:bg-[#d95214] text-white font-bold rounded-xl shadow-lg shadow-[#F36421]/30 transition-all active:scale-95"
        >
          <Plus class="w-5 h-5" /> Nueva Tarifa
        </button>
      </div>

      <!-- Barra de Filtros y Búsqueda -->
      <div class="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col sm:flex-row gap-4">
        <div class="relative flex-1">
          <Search class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            v-model="searchTerm"
            type="text"
            placeholder="Buscar por código, nombre o tipo de vehículo..."
            class="w-full pl-10 pr-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#F36421]/20 focus:border-[#F36421] transition-all"
          >
        </div>
        <select
          v-model="typeFilter"
          class="px-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:outline-none"
        >
          <option value="">Todos los Vehículos</option>
          <option value="MOTO_EXPRESS">🛵 Moto Express</option>
          <option value="CARRO_CARGA">🚗 Carro / Hatchback</option>
          <option value="CAMIONETA_B2B">🛻 Camioneta / Pick-up</option>
          <option value="CAMION_PESADO">🚛 Camión Pesado</option>
        </select>
        <select
          v-model="statusFilter"
          class="px-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:outline-none"
        >
          <option value="">Todos los Estados</option>
          <option value="ACTIVE">Activo</option>
          <option value="INACTIVE">Inactivo</option>
        </select>
      </div>

      <!-- Tabla Operativa de Tarifas -->
      <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
        <div class="overflow-x-auto">
          <table class="w-full text-sm text-left">
            <thead class="text-xs text-slate-500 uppercase bg-slate-50 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800">
              <tr>
                <th class="px-6 py-4 font-semibold">Código & Servicio</th>
                <th class="px-6 py-4 font-semibold">Flota</th>
                <th class="px-6 py-4 font-semibold text-right">Tarifa Base</th>
                <th class="px-6 py-4 font-semibold text-right">Precio / KM</th>
                <th class="px-6 py-4 font-semibold text-right">Parada Extra</th>
                <th class="px-6 py-4 font-semibold text-center">Capacidad</th>
                <th class="px-6 py-4 font-semibold">Estado</th>
                <th class="px-6 py-4 font-semibold text-right">Acciones</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100 dark:divide-slate-800">

              <tr v-if="isLoading">
                <td colspan="8" class="px-6 py-8 text-center text-slate-500">Cargando tarifario LLEVO...</td>
              </tr>

              <tr v-else-if="filteredList.length === 0">
                <td colspan="8" class="px-6 py-8 text-center text-slate-500">No se encontraron tarifas que coincidan.</td>
              </tr>

              <tr
                v-else
                v-for="item in filteredList"
                :key="item.id"
                class="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
              >
                <!-- Código & Nombre -->
                <td class="px-6 py-4">
                  <div class="font-bold text-slate-900 dark:text-slate-100 line-clamp-1">
                    {{ item.name }}
                  </div>
                  <div class="text-xs font-mono font-bold text-[#F36421] mt-0.5">
                    {{ item.code }}
                  </div>
                </td>

                <!-- Tipo de Vehículo -->
                <td class="px-6 py-4">
                  <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                    {{ getFleetIcon(item.service_type) }} {{ getFleetLabel(item.service_type) }}
                  </span>
                </td>

                <!-- Tarifa Base -->
                <td class="px-6 py-4 text-right font-mono font-bold text-slate-800 dark:text-slate-200">
                  C$ {{ formatMoney(item.base_fare) }}
                </td>

                <!-- Precio por Kilómetro -->
                <td class="px-6 py-4 text-right font-mono font-bold text-emerald-600">
                  C$ {{ formatMoney(item.price_per_km) }} /km
                </td>

                <!-- Costo por Parada -->
                <td class="px-6 py-4 text-right font-mono text-slate-600 dark:text-slate-400">
                  C$ {{ formatMoney(item.price_per_stop) }}
                </td>

                <!-- Peso Máximo -->
                <td class="px-6 py-4 text-center">
                  <span class="px-2 py-1 rounded-md text-xs font-bold bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
                    Máx. {{ item.max_weight_kg || 15 }} kg
                  </span>
                </td>

                <!-- Estado -->
                <td class="px-6 py-4">
                  <span
                    class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border"
                    :class="getStatusClasses(item.status)"
                  >
                    <span class="w-1.5 h-1.5 rounded-full" :class="getStatusDot(item.status)"></span>
                    {{ item.status === 'ACTIVE' ? 'Activo' : 'Inactivo' }}
                  </span>
                </td>

                <!-- Acciones -->
                <td class="px-6 py-4 text-right">
                  <div class="flex justify-end gap-2">
                    <button
                      @click="openEdit(item)"
                      class="p-2 text-slate-400 hover:text-[#F36421] hover:bg-orange-50 rounded-lg transition-colors"
                      title="Editar Tarifa"
                    >
                      <FileText class="w-5 h-5" />
                    </button>
                    <button
                      @click="deleteItem(item)"
                      class="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Eliminar Tarifa"
                    >
                      <Trash2 class="w-5 h-5" />
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="px-6 py-4 border-t border-slate-100 dark:border-slate-800 text-xs text-slate-500 flex justify-between items-center">
          <span>Mostrando {{ filteredList.length }} tarifas en el sistema</span>
        </div>
      </div>
    </div>

    <!-- Formulario Modal (Form.vue) -->
    <Form
      :show="showModal"
      :tierToEdit="selectedItem"
      @close="showModal = false"
      @saved="loadData"
    />

  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { Search, Plus, FileText, Trash2 } from 'lucide-vue-next';
import apiClient from '@/api/client';
import Swal from 'sweetalert2';
import { useToast } from '@/composables/useToast';
import Form from './Form.vue'; // Asegúrate de que apunte a tu archivo Form.vue

const list = ref([]);
const isLoading = ref(true);
const searchTerm = ref('');
const statusFilter = ref('');
const typeFilter = ref('');
const showModal = ref(false);
const selectedItem = ref(null);

const { showSuccess, showError } = useToast();

const loadData = async () => {
  isLoading.value = true;
  try {
    const res = await apiClient.get('/service_tiers');
    list.value = Array.isArray(res.data) ? res.data : (res.data.data || []);
  } catch (e) {
    showError('Error cargando el tarifario de LLEVO');
  } finally {
    isLoading.value = false;
  }
};

onMounted(loadData);

const filteredList = computed(() => {
  let result = list.value;
  if (statusFilter.value) result = result.filter(i => i.status === statusFilter.value);
  if (typeFilter.value) result = result.filter(i => i.service_type === typeFilter.value);
  if (searchTerm.value) {
    const term = searchTerm.value.toLowerCase();
    result = result.filter(i => {
      const name = (i.name || '').toLowerCase();
      const code = (i.code || '').toLowerCase();
      const type = (i.service_type || '').toLowerCase();
      return name.includes(term) || code.includes(term) || type.includes(term);
    });
  }
  return result;
});

const formatMoney = (val) => Number(val || 0).toLocaleString('en-US', { minimumFractionDigits: 2 });

const getFleetIcon = (type) => {
  const icons = {
    'MOTO_EXPRESS': '🛵',
    'CARRO_CARGA': '🚗',
    'CAMIONETA_B2B': '🛻',
    'CAMION_PESADO': '🚛'
  };
  return icons[type] || '📦';
};

const getFleetLabel = (type) => {
  const labels = {
    'MOTO_EXPRESS': 'Moto Express',
    'CARRO_CARGA': 'Carro / Hatchback',
    'CAMIONETA_B2B': 'Camioneta Pick-up',
    'CAMION_PESADO': 'Camión Pesado'
  };
  return labels[type] || 'Estándar';
};

const getStatusClasses = (status) => {
  const map = {
    'ACTIVE': 'bg-emerald-50 text-emerald-700 border-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-400',
    'INACTIVE': 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400'
  };
  return map[status] || map['INACTIVE'];
};

const getStatusDot = (status) => {
  const map = { 'ACTIVE': 'bg-emerald-500', 'INACTIVE': 'bg-slate-400' };
  return map[status] || 'bg-slate-400';
};

const openCreate = () => { selectedItem.value = null; showModal.value = true; };
const openEdit = (item) => { selectedItem.value = item; showModal.value = true; };

const deleteItem = (item) => {
  Swal.fire({
    title: '¿Eliminar Tarifa de Servicio?',
    text: "Si hay envíos antiguos con esta tarifa, podrían perder la referencia.",
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#ef4444',
    confirmButtonText: 'Sí, eliminar',
    cancelButtonText: 'Cancelar'
  }).then(async (res) => {
    if (res.isConfirmed) {
      try {
        await apiClient.delete(`/service_tiers/${item.id}`);
        list.value = list.value.filter(i => i.id !== item.id);
        showSuccess('Tarifa eliminada del sistema.');
      } catch (e) {
        showError('No se pudo eliminar la tarifa.');
      }
    }
  });
};
</script>

<style scoped>
@keyframes fade-in { from { opacity: 0; transform: translateY(5px); } to { opacity: 1; transform: translateY(0); } }
.animate-fade-in { animation: fade-in 0.3s ease-out forwards; }
</style>